import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { firebase } from "../../firebase/config";
import "./MailingListAdmin.css";

// The mailing list — its own record of who receives email, NOT a live query
// over members. That distinction is the whole point: someone can be taken off
// the list without deleting their account, and the sync (which only ever adds)
// will never put them back. Same admin gate as the other admin pages.

const SYNC_URL = "https://bc-content-562622614107.us-central1.run.app/api/mailing-list-sync";

const toDate = (ts) => (ts && ts.toDate ? ts.toDate() : ts ? new Date(ts) : null);
const dateOnly = (ts) => {
  const d = toDate(ts);
  return d ? d.toLocaleDateString() : "";
};
const SOURCE_LABEL = { member: "Account", capture: "Email capture", manual: "Added by you", import: "Imported" };
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
const idFor = (email) => String(email).trim().toLowerCase().replace(/\//g, "_");

export default function MailingListPanel() {
  const isAdmin = useSelector((state) => state.auth.a === true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("subscribed");
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [nonMembers, setNonMembers] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    const unsub = firebase
      .firestore()
      .collection("mailingList")
      .onSnapshot(
        (snap) => {
          const next = [];
          snap.forEach((doc) => next.push({ id: doc.id, ...doc.data() }));
          next.sort((a, b) => String(a.email).localeCompare(String(b.email)));
          setRows(next);
          setLoading(false);
        },
        (err) => {
          setError((err && err.message) || "Failed to load the list.");
          setLoading(false);
        }
      );
    return () => unsub();
  }, [isAdmin]);

  const counts = useMemo(() => {
    const c = { subscribed: 0, unsubscribed: 0, member: 0, manual: 0 };
    rows.forEach((r) => {
      if (r.status === "unsubscribed") c.unsubscribed += 1;
      else c.subscribed += 1;
      if (r.source === "member") c.member += 1;
      if (r.source === "manual") c.manual += 1;
    });
    return c;
  }, [rows]);

  const toMs = (ts) => (ts ? (typeof ts.toMillis === "function" ? ts.toMillis() : new Date(ts).getTime() || 0) : 0);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (filter === "subscribed" && r.status === "unsubscribed") return false;
      if (filter === "unsubscribed" && r.status !== "unsubscribed") return false;
      if (!needle) return true;
      return `${r.email} ${r.name || ""}`.toLowerCase().includes(needle);
    });
    // Paul, 7 Sep 2026: "organise the unsub list by date, so I can keep an eye on who unsubs" -
    // the unsubscribed view is newest first; the other views stay alphabetical.
    if (filter === "unsubscribed") list.sort((a, b) => toMs(b.unsubscribedAt) - toMs(a.unsubscribedAt));
    return list;
  }, [rows, q, filter]);

  const unsubLast7 = useMemo(() => {
    const cut = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return rows.filter((r) => r.status === "unsubscribed" && toMs(r.unsubscribedAt) >= cut).length;
  }, [rows]);

  const setStatus = async (row, status) => {
    setBusy(row.id);
    try {
      await firebase.firestore().collection("mailingList").doc(row.id).set(
        {
          status,
          unsubscribedAt: status === "unsubscribed" ? firebase.firestore.FieldValue.serverTimestamp() : null,
        },
        { merge: true }
      );
    } catch (e) {
      setError((e && e.message) || "Could not update.");
    }
    setBusy("");
  };

  const addPerson = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!isEmail(email)) { setError("That doesn't look like an email address."); return; }
    setBusy("add");
    setError("");
    try {
      // merge so re-adding an unsubscribed person resubscribes them rather
      // than wiping how they originally arrived.
      await firebase.firestore().collection("mailingList").doc(idFor(email)).set(
        {
          email,
          name: newName.trim(),
          status: "subscribed",
          source: "manual",
          addedAt: firebase.firestore.FieldValue.serverTimestamp(),
          addedBy: "admin",
          unsubscribedAt: null,
        },
        { merge: true }
      );
      setNewEmail("");
      setNewName("");
    } catch (e2) {
      setError((e2 && e2.message) || "Could not add.");
    }
    setBusy("");
  };

  const runSync = async () => {
    setBusy("sync");
    setError("");
    try {
      const user = firebase.auth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const res = await fetch(SYNC_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Sync failed");
      setCopied(`Sync added ${data.added} new — ${data.total} on the list`);
      setTimeout(() => setCopied(""), 6000);
    } catch (e) {
      setError((e && e.message) || "Sync failed.");
    }
    setBusy("");
  };

  const copyEmails = async () => {
    const list = shown.filter((r) => r.status !== "unsubscribed").map((r) => r.email).join(", ");
    try {
      await navigator.clipboard.writeText(list);
      setCopied(`Copied ${shown.filter((r) => r.status !== "unsubscribed").length} addresses`);
      setTimeout(() => setCopied(""), 4000);
    } catch (e) {
      setError("Could not copy — select the box below instead.");
    }
  };

  // Subscribed people who are NOT current paying members — the conversion
  // cohort. Membership mirrors the App.js access formula: a future
  // subscriptionExpires (+3-day renewal grace) means access; anything else,
  // including stale subscriptionActive flags with long-past expiries, doesn't.
  const copyNonMembers = async () => {
    setBusy("nonmem");
    setError("");
    try {
      const snap = await firebase.firestore().collection("members").get();
      const GRACE_MS = 3 * 24 * 60 * 60 * 1000;
      const activeEmails = new Set();
      // accessRemoved: accounts whose access Paul deliberately ended.
      // pitchExempt: own/family/duplicate accounts. Neither is ever a
      // conversion target, however lapsed they look.
      const excludedEmails = new Set();
      snap.forEach((doc) => {
        const d = doc.data() || {};
        const email = d.email ? String(d.email).trim().toLowerCase() : null;
        if (!email) return;
        if (d.accessRemoved === true || d.pitchExempt === true) { excludedEmails.add(email); return; }
        const exp = d.subscriptionExpires;
        const expMs = exp
          ? (typeof exp.toMillis === "function" ? exp.toMillis() : new Date(exp).getTime())
          : 0;
        if (expMs + GRACE_MS > Date.now()) activeEmails.add(email);
      });
      const list = rows
        .filter((r) => {
          const email = String(r.email).trim().toLowerCase();
          return r.status !== "unsubscribed" && !activeEmails.has(email) && !excludedEmails.has(email);
        })
        .map((r) => r.email);
      setNonMembers(list);
      await navigator.clipboard.writeText(list.join(", "));
      setCopied(`Copied ${list.length} non-member addresses (BCC-ready)`);
      setTimeout(() => setCopied(""), 6000);
    } catch (e) {
      setError((e && e.message) || "Could not build the non-member list.");
    }
    setBusy("");
  };

  if (!isAdmin) return null;

  const subscribedShown = shown.filter((r) => r.status !== "unsubscribed");

  return (
    <div className="MailingList">
      <div className="MailingList-head">
        <h1 className="MailingList-title">Mailing list</h1>
        <div className="MailingList-stats">
          <span><strong>{counts.subscribed}</strong> subscribed</span>
          <span><strong>{counts.unsubscribed}</strong> unsubscribed</span>
          <span><strong>{unsubLast7}</strong> unsubscribed in the last 7 days</span>
          <span><strong>{counts.member}</strong> from accounts</span>
          <span><strong>{counts.manual}</strong> added by you</span>
        </div>
        <p className="MailingList-note">
          This list is its own thing. Removing someone here does <strong>not</strong> touch their
          account, and Sync only ever adds — an unsubscribe is never undone.
        </p>
      </div>

      <form className="MailingList-add" onSubmit={addPerson}>
        <input className="MailingList-input" placeholder="email to add" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
        <input className="MailingList-input" placeholder="name (optional)" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <button className="MailingList-btn" type="submit" disabled={busy === "add"}>
          {busy === "add" ? "Adding…" : "Add person"}
        </button>
        <button className="MailingList-btn MailingList-btn--ghost" type="button" onClick={runSync} disabled={busy === "sync"}>
          {busy === "sync" ? "Syncing…" : "Sync from accounts"}
        </button>
      </form>

      <div className="MailingList-tools">
        <input className="MailingList-input MailingList-input--search" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="MailingList-filters">
          {["subscribed", "unsubscribed", "all"].map((f) => (
            <button key={f} type="button" className={`MailingList-chip${filter === f ? " is-on" : ""}`} onClick={() => setFilter(f)}>
              {f}
            </button>
          ))}
        </div>
        <button className="MailingList-btn" type="button" onClick={copyEmails}>
          Copy {subscribedShown.length} emails
        </button>
        <button className="MailingList-btn MailingList-btn--ghost" type="button" onClick={copyNonMembers} disabled={busy === "nonmem"}>
          {busy === "nonmem" ? "Working…" : "Copy non-members (BCC)"}
        </button>
      </div>

      {copied ? <p className="MailingList-ok">{copied}</p> : null}
      {error ? <p className="MailingList-err">{error}</p> : null}

      {loading ? (
        <p className="MailingList-empty">Loading…</p>
      ) : (
        <>
          <table className="MailingList-table">
            <thead>
              <tr><th>Email</th><th>Name</th><th>Source</th><th>Added</th><th>Status</th><th>Unsubscribed</th><th /></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className={r.status === "unsubscribed" ? "is-off" : ""}>
                  <td>{r.email}</td>
                  <td>{r.name || "—"}</td>
                  <td>{SOURCE_LABEL[r.source] || r.source || "—"}</td>
                  <td>{dateOnly(r.addedAt)}</td>
                  <td>{r.status === "unsubscribed" ? "Unsubscribed" : "Subscribed"}</td>
                  <td>
                    {r.status === "unsubscribed" && r.unsubscribedAt
                      ? `${dateOnly(r.unsubscribedAt)}${r.unsubscribedVia === "self-serve-link" ? " · via the link" : r.unsubscribedVia ? ` · ${r.unsubscribedVia}` : " · by you"}`
                      : "—"}
                  </td>
                  <td>
                    <button
                      className="MailingList-btn MailingList-btn--small"
                      disabled={busy === r.id}
                      onClick={() => setStatus(r, r.status === "unsubscribed" ? "subscribed" : "unsubscribed")}
                    >
                      {r.status === "unsubscribed" ? "Re-subscribe" : "Remove"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!shown.length ? <p className="MailingList-empty">Nobody matches that.</p> : null}

          <label className="MailingList-copyLabel">Copy-paste block ({subscribedShown.length})</label>
          <textarea className="MailingList-copybox" readOnly value={subscribedShown.map((r) => r.email).join(", ")} />

          {nonMembers ? (
            <>
              <label className="MailingList-copyLabel">
                Non-members — conversion BCC ({nonMembers.length}). Excludes paying members and access-removed accounts.
              </label>
              <textarea className="MailingList-copybox" readOnly value={nonMembers.join(", ")} />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
